import React, { FormEvent } from 'react'

import { getCandidateAsns, ReceivingFlowController } from '../flow'
import { ActionButton, RfShell } from '../components/RfShell'
import { RfScanField } from '../components/RfScanField'
import { RfStatusBanner } from '../components/RfStatusBanner'
import {
  CandidateButton,
  CandidateList,
  DecisionActions,
  DecisionButton,
  DecisionDialog,
  DecisionNotice,
  DialogBackdrop,
  ScanForm,
  WarningTaskButton,
  TaskHeader,
} from '../styles'

interface AsnSelectionScreenProps {
  controller: ReceivingFlowController
  embedded?: boolean
  onExit: () => void
}

export const AsnSelectionScreen = ({ controller, embedded = false, onExit }: AsnSelectionScreenProps) => {
  const { state, dispatch } = controller
  const candidates = getCandidateAsns(state)

  const submitAsn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    dispatch({ type: 'submit-asn-selection' })
  }

  const exceptionDialog = state.exceptionDialogOpen ? (
    <DialogBackdrop>
      <DecisionDialog role="dialog" aria-modal="true" aria-labelledby="exception-asn-title">
        <h2 id="exception-asn-title">Create exception ASN?</h2>
        <p>This receipt will be recorded without an existing ASN.</p>
        <DecisionNotice>The missing ASN will be flagged for review.</DecisionNotice>
        <DecisionActions>
          <DecisionButton type="button" autoFocus onClick={() => dispatch({ type: 'close-exception-dialog' })}>
            Cancel
          </DecisionButton>
          <DecisionButton type="button" $primary onClick={() => dispatch({ type: 'confirm-exception-asn' })}>
            Create ASN
          </DecisionButton>
        </DecisionActions>
      </DecisionDialog>
    </DialogBackdrop>
  ) : null

  return (
    <RfShell
      title="Receiving"
      phase="Identify ASN"
      embedded={embedded}
      contextSummary={state.reference ? `Reference: ${state.reference}` : undefined}
      contextDetails={null}
      onBack={() => dispatch({ type: 'back' })}
      moreActions={[{ label: 'Exit receiving', onSelect: onExit, danger: true }]}
      overlay={exceptionDialog}
      actions={
        <ActionButton type="submit" form="asn-selection-form">
          Continue
        </ActionButton>
      }
    >
      {state.status ? <RfStatusBanner tone={state.status.tone}>{state.status.message}</RfStatusBanner> : null}
      <TaskHeader>
        <h1>Select or enter ASN</h1>
      </TaskHeader>

      {candidates.length > 0 ? (
        <CandidateList aria-label="Candidate ASNs">
          {candidates.map((asn) => (
            <CandidateButton
              key={asn.id}
              type="button"
              $selected={state.selectedAsn?.id === asn.id}
              aria-pressed={state.selectedAsn?.id === asn.id}
              onClick={() => dispatch({ type: 'select-asn', asnId: asn.id })}
            >
              <strong>{asn.asnNumber}</strong>
              <span>
                {asn.company} · {asn.lineCount} lines / {asn.expectedUnits.toLocaleString('en-US')} eaches
              </span>
            </CandidateButton>
          ))}
        </CandidateList>
      ) : null}

      <ScanForm id="asn-selection-form" aria-label="Select or enter ASN" onSubmit={submitAsn}>
        <RfScanField
          label="ASN number"
          value={state.asnEntry}
          placeholder="Scan or enter ASN"
          hint="Use an active inbound ASN."
          onChange={(asnNumber) => dispatch({ type: 'set-asn-entry', asnNumber })}
        />
      </ScanForm>

      <WarningTaskButton type="button" onClick={() => dispatch({ type: 'open-exception-dialog' })}>
        Create ASN
      </WarningTaskButton>
    </RfShell>
  )
}
